<script>
/**
 * # ExpandableRows/ExpandableItems
 * Dynamic Expandable Row
 *
 * @deprecated Do not copy this pattern. See https://apiabroad.atlassian.net/browse/JUAN-2217
 *
 */

export default {
  name: "ExpandableRows",
};
</script>

<script setup>
import { computed, ref, toRefs } from "vue";
import { pickBy, identity } from "lodash";
import { PencilIcon } from "@heroicons/vue/24/outline";
import { ChevronRightIcon } from "@heroicons/vue/20/solid";
import { FlexRender } from "@tanstack/vue-table";
import Spinner from "@/components/helpers/Spinner.vue";
import { COLUMNS_IDS } from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/constants";
import { LAST_TIME_UPDATED_LITERAL } from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/constants";
import {
  usePriceOverrides,
} from "@/composables/authorization";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
  row: {
    type: [Array, Object],
    default: () => [],
  },
  label: {
    type: String,
    default: "",
  },
  expandEnabled: {
    type: Boolean,
    default: true,
  },
  editEnabled: {
    type: Boolean,
    default: true,
  },
  data: {
    type: Object,
    default: () => {},
  },
  loading: {
    type: Boolean,
    default: false,
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  itemNameRouteSetup: {
    type: Object,
    default: () => {},
  },
  expandContentClass: {
    type: String,
    default: "grid grid-cols-5 gap-4",
  },
});

const {
  expandEnabled,
  editEnabled,
  loading,
  row,
  label,
  data,
  itemNameRouteSetup,
  expandContentClass,
} = toRefs(props);

const emit = defineEmits(["openStatusModal", "expanded"]);
const open = ref(false);

const expand = () => {
  open.value = !open.value;
  emit("expanded", open.value);
};
</script>

<template>
  <tr
    class="border-t-[1px] border-solid border-t-gray-200 w-full hover:bg-blue-350"
    :class="[open && expandEnabled ? 'bg-gray-200' : '']"
  >
    <td
      v-for="cell in row.getVisibleCells()"
      :key="cell.column.id"
      class="py-8 text-md border-t-[1px] border-solid border-t-gray-200"
    >
      <div
        v-if="
          cell.column.id.trim() === COLUMNS_IDS.more_info &&
          cell.row.original[COLUMNS_IDS.more_info]
        "
        class="flex flex-col capitalize"
      >
        <span
          v-for="(item, index) in Object.values(
            cell.row.original[COLUMNS_IDS.more_info]
          )"
          :key="index"
        >
          {{ item || "" }}
        </span>
      </div>

      <div
        v-else-if="cell.column.id.trim() === COLUMNS_IDS.name"
        class="flex flex-col"
      >
        <span
          v-for="item in Object.values(cell.row.original[COLUMNS_IDS.name])"
          v-if="cell.row.original[COLUMNS_IDS.name]"
          class="first:text-teal-900 first:capitalize"
        >
          <router-link
            v-if="
              row.original?.id &&
              itemNameRouteSetup?.[label]?.name &&
              itemNameRouteSetup?.[label]?.permission
            "
            :to="{
              name: itemNameRouteSetup[label].name,
              params: {
                [itemNameRouteSetup[label].extraParamName]: row.original.id,
              },
            }"
            target="_blank"
          >
            {{ item }}
          </router-link>
          <span v-else>{{ item }}</span>
        </span>
      </div>

      <button
        v-else-if="cell.column.id.trim() === COLUMNS_IDS.action"
        class="flex justify-center items-center ml-4 text-md font-bold bg-transparent text-teal-900"
        :disabled="!editEnabled"
        @click="emit('openStatusModal', row.original.id)"
      >
        <PencilIcon class="h-5" />
      </button>

      <router-link
        v-else-if="
          cell.column.id.trim() === COLUMNS_IDS.name &&
          row.original?.id &&
          itemNameRouteSetup?.[label]?.name &&
          itemNameRouteSetup?.[label]?.permission
        "
        :to="{
          name: itemNameRouteSetup[label].name,
          params: {
            [itemNameRouteSetup[label].extraParamName]: row.original.id,
          },
        }"
        target="_blank"
      >
        {{ cell.getValue() }}
      </router-link>

      <div
        v-else-if="
          cell.column.id.trim() === COLUMNS_IDS.totalPrice &&
          row.original?.priceOverrideStatus?.display
        "
        class="flex items-center gap-1"
      >
        <span>{{ row.original[COLUMNS_IDS.totalPrice] }}</span>
        <span
          v-if="usePriceOverrides"
          class="material-symbols-outlined"
          :class="row.original.priceOverrideStatus.labelColor"
        >
          {{ row.original.priceOverrideStatus.icon }}
        </span>
      </div>

      <FlexRender
        v-else-if="cell.column.id.trim() !== COLUMNS_IDS.type"
        :render="cell.column.columnDef.cell"
        :props="cell.getContext()"
      />

      <div v-else class="flex">
        <button
          v-if="expandEnabled"
          :class="['text-md font-semibold items-center cursor-pointer']"
        >
          <ChevronRightIcon
            :class="[
              {
                'rotate-90 transform': open,
              },
              'h-6 w-6 inline-block',
            ]"
            @click="() => expand()"
          />
        </button>

        <div
          class="flex text-md font-semibold justify-start items-center"
          :class="[!expandEnabled ? 'pl-4' : '']"
        >
          <span>{{ label }}</span>
        </div>
      </div>
    </td>
  </tr>
  <tr v-if="row?.original?.[LAST_TIME_UPDATED_LITERAL]">
    <td colspan="20" class="italic text-right pr-8">
      Last updated: {{ row?.original?.[LAST_TIME_UPDATED_LITERAL] }}
    </td>
  </tr>
  <tr v-if="open && expandEnabled">
    <td colspan="10" class="p-4">
      <div v-if="!loading" :class="expandContentClass">
        <div
          v-for="(value, key) in pickBy(data, identity)"
          :key="key"
          class="flex flex-col"
        >
          <slot :name="key" :value="value">
            <span class="uppercase text-gray-900 text-md font-semibold">
              {{ key }}
            </span>
            <span class="text-md">
              {{ value }}
            </span>
          </slot>
        </div>
      </div>
      <Spinner v-else class="relative" />
    </td>
  </tr>
</template>
